import { Box, Button, CircularProgress, Grid, TextField, Typography } from "@mui/material"
import { ChangeEvent, KeyboardEvent } from "react"
import { InfoBox } from "../../components/DOAAdminComponents/InfoBox"
import { ResponseAlert } from "../../components/DOAAdminComponents/ResponseAlert"
import { MainContainer } from "../../components/MainContainerComp"
import { PlateModal } from "../../components/PlateModal"
import { IDOAShift } from "../availability-administration/AvailabilityAdministrationData"
import { IEmployee } from "./AvailabilityAdministrationData"
import { AvailableCode, SUCCESS_MSG } from "./AvailabilityAdministrationModel"
import { FormControls } from "./components/FormControls"
import { StatusInfo } from "./components/StatusInfo"
import { buttonContainStyle, errorStyle, inputStyle, labelStyle, warningWrapper } from "./styled"

interface IAvailabilityAdministrationProps {
  shift?: IDOAShift,
  onSearchEmployee: () => void;
  onSearchEnter: (event: KeyboardEvent<HTMLInputElement>) => void;
  onInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
  regId: string,
  selectedRegId: string,
  employeeData: IEmployee,
  onOpenPlate: () => void;
  openPlateModal: boolean;
  pendingMakeAvailable: boolean;
  onMakeMeAvailable: () => void;
  doaSelection: AvailableCode;
  onCheckboxChange: (code: AvailableCode) => void;
  actionMsg?: string,
  onFetchEmployeeData: () => void,
  isDispatchTypeSix: boolean,
  isNightDispatch: boolean,
}

export const AvailabilityAdministrationEmployee: React.FC<IAvailabilityAdministrationProps> = ({
  shift,
  onSearchEmployee,
  onInputChange,
  onSearchEnter,
  regId,
  selectedRegId,
  employeeData,
  onOpenPlate,
  openPlateModal,
  pendingMakeAvailable,
  onMakeMeAvailable,
  doaSelection,
  onCheckboxChange,
  actionMsg,
  onFetchEmployeeData,
  isDispatchTypeSix,
  isNightDispatch
}) => {
  const availableForBothDoaSelection = AvailableCode.OPTION_1 + AvailableCode.OPTION_2 - AvailableCode.AVAILABLE;
  const employeeName = (employeeData?.empInfo?.fullName && employeeData?.empInfo?.fullName.split('-')[0]) || '';
  const employeeBoardType = (employeeData?.empInfo?.fullName && employeeData?.empInfo?.fullName.split('-')[1]) || '';
  const availableForBothEmployee = doaSelection === availableForBothDoaSelection;

  const availableForFirstOption = (doaSelection === AvailableCode.OPTION_1) ||
    availableForBothEmployee;

  const availableForSecondOption = (doaSelection === AvailableCode.OPTION_2) ||
    availableForBothEmployee;

  const isSetAvailability =
    isDispatchTypeSix || isNightDispatch;

  const buttonLabel = pendingMakeAvailable
    ? "Processing..."
    : isSetAvailability
      ? "Set Availability"
      : !employeeData?.empInfo?.doaSelections
        ? "Make Available"
        : "Make Unavailable";

  const { doaSelections, dependentShift, canMakeAvailable, makeAvailableMessage } = employeeData?.empInfo || {};
  const hasEmployeeInfo = !!employeeData?.empInfo && Object.keys(employeeData.empInfo).length > 0;
  const isSelectionMatchedForTypeSix = doaSelection === doaSelections && isDispatchTypeSix;

  const isSelectionUnchangedForNightDispatch = isNightDispatch && (
    (doaSelection === availableForBothDoaSelection && !!doaSelections && !!dependentShift?.doaSelections) ||
    (doaSelection === AvailableCode.NOT_AVAILABLE && !doaSelections && !dependentShift?.doaSelections) ||
    (doaSelection === AvailableCode.OPTION_1 && !!doaSelections && !dependentShift?.doaSelections) ||
    (doaSelection === AvailableCode.OPTION_2 && !doaSelections && !!dependentShift?.doaSelections)
  );

  const cannotBeMadeAvailableConditions = isNightDispatch
    ? (!!makeAvailableMessage && !canMakeAvailable && !dependentShift?.canMakeAvailable)
    : !!makeAvailableMessage && !canMakeAvailable;

  const disabledMakeAvailableButton =
    !hasEmployeeInfo ||
    isSelectionMatchedForTypeSix ||
    pendingMakeAvailable ||
    isSelectionUnchangedForNightDispatch ||
    cannotBeMadeAvailableConditions

  if (!shift) {
    return <CircularProgress />
  }
  return (
    <MainContainer>
      <Typography
        component={'div'}
        variant='container'
        data-automation="data-container"
      >
        <InfoBox
          date={shift.dispatchDate}
          dispatchDescription={shift.shiftDescription}
          dispatchStatus={shift.physicalDspatchStatus}
          availabilityStatus={shift.availabilityStatus}
          dispatch={shift.shift}
        />
        <Typography
          component='div'
          variant="availability-administration-card"
        >
          <Box className='number-wrapper'>
            <Grid container>
              <Grid item xs={0} md={3} />
              <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={labelStyle} data-automation="reg-num-lbl">Emp. #</Typography>
                    <TextField
                      value={regId}
                      onChangeCapture={onInputChange}
                      onKeyUp={onSearchEnter}
                      sx={inputStyle}
                      id="standard-number"
                      type="number"
                      inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                      }}
                      data-automation="reg-num-txt"
                    />
                    <Button
                      disabled={!regId}
                      variant="contained"
                      size="medium"
                      onClick={onSearchEmployee}
                      data-automation="search-emp-btn"
                      sx={buttonContainStyle}
                    >
                      Search
                    </Button>
                  </Box>
                  {(!!employeeData?.message || !!employeeData?.empInfo.makeAvailableMessage) &&
                    <Typography sx={errorStyle} data-automation="error-msg">
                      {employeeData?.message || employeeData.empInfo.makeAvailableMessage}
                    </Typography>}
                  {shift && <FormControls
                    availableForFirstOption={availableForFirstOption}
                    availableForSecondOption={availableForSecondOption}
                    isNightDispatch={isNightDispatch}
                    isDispatchTypeSix={isDispatchTypeSix}
                    employeeData={employeeData}
                    onCheckboxChange={onCheckboxChange}
                  />}
                </Box>
              </Grid>
              <Grid item xs={0} md={3} />
            </Grid>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Button
              disabled={disabledMakeAvailableButton}
              variant="contained"
              size="medium"
              onClick={onMakeMeAvailable}
              data-automation="make-avail-btn"
              sx={buttonContainStyle}
            >
              {buttonLabel}
            </Button>
          </Box>
          <Box
            className="employee-info"
            data-automation="emp-info-container"
          >
            {
              employeeData?.empInfo && Object.keys(employeeData.empInfo).length !== 0 && <>
                <Typography data-automation="emp-info-labels">
                  {employeeName} - <Typography component='span' data-automation="emp-info-values">{employeeBoardType}</Typography>
                </Typography>
                <Typography data-automation="emp-info-labels">
                  Home Port: <Typography component='span' data-automation="emp-info-values">{employeeData.empInfo.homePort}</Typography>
                </Typography>
                <Typography data-automation="emp-info-labels">
                  Board and Plate: <Typography component='span' data-automation="emp-info-values">{employeeData.empInfo.boardPlateDescription}</Typography>
                </Typography>
                {employeeData.empInfo.primaryPhoneNumber &&
                  <Typography data-automation="emp-info-labels">
                    Primary Phone: <Typography component='span' data-automation="emp-info-values">{employeeData.empInfo.primaryPhoneNumber}</Typography>
                  </Typography>
                }
                {employeeData.empInfo.alternatePhoneNumber &&
                  <Typography data-automation="emp-info-labels">
                    Alternate Phone: <Typography component='span' data-automation="emp-info-values">{employeeData.empInfo.alternatePhoneNumber}</Typography>
                  </Typography>
                }
                {employeeData.empInfo.cellPhoneNumber &&
                  <Typography data-automation="emp-info-labels">
                    Cell: <Typography component='span' data-automation="emp-info-values">{employeeData.empInfo.cellPhoneNumber}</Typography>
                  </Typography>
                }
                <Typography data-automation="emp-info-labels">
                  Last Worked: <Typography component='span' data-automation="emp-info-values">{employeeData.empInfo.lastWorkedDate}</Typography>
                </Typography>
                <Box sx={{ textAlign: 'center' }}>
                  <Button variant="outlined" onClick={onOpenPlate} data-automation="emp-plate-btn">{employeeData.empInfo.boardPlate}</Button>
                </Box>
                {employeeData.empInfo.tscClearance &&
                  <Box sx={warningWrapper}>
                    {employeeData.empInfo.hasTsc && <img src="/assets/images/sticker_tcs_green.gif" alt="clearance" data-automation="emp-transcanada-img" />}
                    <Typography component='span' data-automation="emp-transcanada-txt">{employeeData.empInfo.tscClearance}</Typography>
                  </Box>
                }
                {employeeData.empInfo.suspensionNotes &&
                  <Box sx={warningWrapper}>
                    <img src="/assets/images/warning1.gif" alt="clearance" data-automation="emp-susp-img" />
                    <Typography sx={{ display: 'inline-block' }}>
                      <Typography component='span' sx={{ fontWeight: 'bold', mr: 1 }} data-automation="emp-susp-lbl">Suspensions/Restrictions:</Typography>
                      <Typography component='span' data-automation="emp-susp-val">{employeeData.empInfo?.suspensionNotes}</Typography>
                    </Typography>
                  </Box>
                }
                {employeeData.empInfo.squamishComplement &&
                  <Typography sx={{ textAlign: 'center' }} data-automation="emp-sq-comp-lbl">{employeeData.empInfo.squamishComplement}</Typography>
                }
                {employeeData.empInfo.rwf &&
                  <Typography sx={{ textAlign: 'center' }} data-automation="emp-rwf-lbl">{employeeData.empInfo.rwf}</Typography>
                }
              </>
            }
          </Box>
          <StatusInfo
            availableForBothDoaSelection={availableForBothDoaSelection}
            availableForFirstOption={availableForFirstOption}
            availableForSecondOption={availableForSecondOption}
            isDispatchTypeSix={isDispatchTypeSix}
            isNightDispatch={isNightDispatch}
            shift={shift}
            employeeData={employeeData}
            actionMsg={actionMsg}
            selectedRegId={selectedRegId}
          />
        </Typography>
        <ResponseAlert
          description='You won’t be able to submit your changes at this time. The page will refresh when you click on the [OK] button below.'
          title={`The declaration window has expired for the ${shift.dispatchDate} - ${shift.shiftDescription} Shift!`}
          onClose={() => onFetchEmployeeData()}
          open={actionMsg !== SUCCESS_MSG && !!actionMsg}
        />
        <PlateModal
          id={Number(regId)}
          onClose={onOpenPlate}
          open={openPlateModal}
          board={employeeData?.empInfo.board}
        />
      </Typography>
    </MainContainer >
  )
}